<template>
  <section id="comments" class="comments">
    <div :class="commentTopperClass">
      <header class="comments__header">
        <h2 class="sr-only">
          Comments
        </h2>
        <p class="heading -medium">
          {{ totalCommentsText }}
        </p>
        <div class="flex-right">
          <a class="link small demi" href="/page/discussion-policy">Discussion Policy</a>
        </div>
      </header>

      <div class="content">
        <img v-if="user.avatar" :src="user.avatar" alt="" />
        <span v-else-if="user.isLoggedIn && !user.avatar" class="avatar-placeholder demi">{{
          user.displayName.charAt(0)
        }}</span>

        <comment-form v-if="user.isLoggedIn" :handle-focus="focusForm" />

        <div v-else class="sign-in-to-reply">
          <a :href="`/login?from=${currentPath}`" class="btn -main -block -large-medium-only"
            >Sign In to Reply</a
          >
        </div>
      </div>
    </div>

    <header v-if="comments.length > 0" class="comments__header">
      <div class="comments__sort flex-right">
        <div class="form__group -inline">
          <label for="sort">Sort</label>
          <div class="select-style">
            <form>
              <select
                id="sort"
                v-model="sort"
                name="sort"
                @change="handleSort($event.target.value)"
              >
                <option value="newest">
                  Newest First
                </option>
                <option value="popular">
                  Most Popular
                </option>
                <option value="oldest">
                  Oldest First
                </option>
              </select>
            </form>
          </div>
        </div>
      </div>
    </header>

    {{/* initial loader */}}
    <comment-loader
      v-if="loading && loadingParent < 0 && loadingAmount === 1"
      id="comment-loader"
    />

    <comment v-for="comment in comments" :key="comment.id" :comment="comment" />

    <button
      v-if="!loading && showMoreCommentsBtn && comments.length > 0"
      class="btn -block -link -large-medium-only load-more-comments"
      @click="loadMoreComments()"
    >
      Load More Comments
    </button>

    {{/* pagination loader */}}
    <comment-loader v-if="loading && loadingParent < 0 && loadingAmount > 1" id="comment-loader" />

    <modal-flag
      v-if="flagModal.active"
      :id="flagModal.id"
      flaggable-type="comment"
      :handle-close-click="closeFlagModal"
    />

    <modal-confirm
      v-if="deleteConfirmationModal.active"
      heading="Remove Comment"
      subheading="Are you sure you want to remove your comment?  All replies to the comment will be removed as well."
      button-text="Remove"
      :handle-close-click="closeDeleteConfirmationModal"
      :handle-confirm-click="handleDeleteComment"
    />
  </section>
</template>

<script>
// @flow
import comment from './comment.vue'
import commentLoader from './comment-loader.vue'
import commentForm from './comment-form.vue'
import modalConfirm from 'app/components/modals/modal-confirm.vue'
import modalFlag from 'app/components/modals/modal-flag.vue'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import { PARENT_LOAD_AMOUNT } from 'app/constants'
import { fbAPIDelete, fbAPIGet } from 'app/utils/http-api'
import { buildComment } from 'app/utils/comments'

export default {
  components: {
    modalFlag,
    modalConfirm,
    comment,
    commentForm,
    commentLoader,
  },
  data() {
    return {
      canLoadMore: true,
      firstLoad: true,
    }
  },
  computed: {
    commentTopperClass() {
      return `comments__topper${this.user.isLoggedIn ? ' -form' : ''}`
    },
    currentPath() {
      return this.$route.path
    },
    showMoreCommentsBtn() {
      return (
        (Laravel.commentId && this.firstLoad) ||
        (this.canLoadMore && this.comments.length > PARENT_LOAD_AMOUNT - 1)
      )
    },
    ...mapState({
      /* eslint-disable */
      comments: (state: State) => state.comments.comments,
      form: (state: State) => state.comments.commentForm,
      loading: (state: State) => state.comments.commentsLoading.status,
      loadingAmount: (state: State) => state.comments.commentsLoading.amount,
      loadingParent: (state: State) => state.comments.commentsLoading.parent,
      sort: (state: State) => state.comments.commentSort,
      /* eslint-enable */
    }),
    ...mapState(['flagModal', 'deleteConfirmationModal', 'user']),
    ...mapGetters(['totalCommentsText']),
  },
  methods: {
    handleDeleteComment() {
      const commentId = this.deleteConfirmationModal.id

      fbAPIDelete(
        `comment/${commentId}`,
        res => {
          this.closeDeleteConfirmationModal()

          let updatedComments = this.comments.filter(comment => comment.id !== commentId)
          updatedComments = updatedComments.map(comment => {
            comment.children = comment.children.filter(child => child.id !== commentId)
            comment.userAdded = comment.userAdded.filter(child => child.id !== commentId)
            return comment
          })
          this.replaceComments(updatedComments)
        },
        err => {
          this.closeDeleteConfirmationModal()
        },
      )
    },

    loadMoreComments() {
      const { commentableType, commentableId } = this.$store.state.comments
      const offset = this.comments.length

      // if on page that loads just one comment, offset additional calls by one
      const adjOffset = Laravel.commentId ? offset - 1 : offset

      // figure out starting comment to help with sort based pagination (offsets)
      // as there is an issue of comments not showing on popular discussions
      // that are getting lots of real time comments (when the CTA is clicked)
      let start = 0

      if (this.comments && this.comments.length) {
        start = this.comments[0].id

        // switch start to be from popular count
        if (this.sort === 'popular') {
          start = this.comments[0].likeCount
        }
      }
      // end start

      // xhr
      const url = `comments/${commentableType}/${commentableId}?sort=${this.sort}&start=${start}&offset=${adjOffset}`

      this.form.active && this.closeCommentForm()

      this.setCommentsLoading({ amount: PARENT_LOAD_AMOUNT })

      fbAPIGet(
        url,
        res => {
          this.firstLoad = false

          if (!res.body.data || res.body.data.length === 0) {
            this.canLoadMore = false
          } else {
            this.addComments(res.body.data.map(buildComment))
            this.canLoadMore = res.body.data.length === PARENT_LOAD_AMOUNT
          }
          this.hideLoading()
        },
        err => {
          this.hideLoading()
        },
      )
    },

    handleSort(value) {
      this.sortAndReplaceComments(value)

      // reset as sort replaces results
      this.canLoadMore = true
    },
    ...mapActions([
      'focusForm',
      'sortAndReplaceComments',
      'showErrorModal',
      'showLoginModal',
      'hideLoading',
    ]),
    ...mapMutations([
      'replaceComments',
      'addComments',
      'closeFlagModal',
      'closeDeleteConfirmationModal',
      'closeCommentForm',
      'setCommentsLoading',
    ]),
  },
}
</script>
